import {
  ChevronRight,
  EditOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import clsx from "clsx";
import { useCallback, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";

import { FramerTooltip } from "@ryde/controls";
import {
  formatDate,
  formatPhoneLocal,
  NO_VALUE_DISPLAY,
  TOOLTIP_DELAY_BUTTONS,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import {
  IncidentPopupRoot,
  LiveMapIncidentTooltipStackStyled,
  LiveMapIncidentTypeColorDotStyled,
} from "../live-map.incidents-layer.styled";
import { incidentPopupCn } from "./live-map.incidents-layer.constants";
import type { ILiveMapIncidentPopupViewProps } from "./live-map.incidents-layer.models";

export const LiveMapIncidentPopupView: FC<ILiveMapIncidentPopupViewProps> = ({
  incident,
  isPreviewMarker,
  dateTimeRowLabel,
  rideNumberRowLabel,
  driverRowLabel,
  closeLabel,
  openIncidentTooltip,
  timezone,
  dateTimeFormat,
  onCloseIncident,
  onOpenIncidentDrawer,
  onRequestClosePopup,
  showCloseIncidentButton,
  showOpenIncidentButton,
  canEditIncident,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const onClickClose = useCallback(async () => {
    if (isPreviewMarker || busy) {
      return;
    }

    setBusy(true);

    try {
      const result = await onCloseIncident();

      // expect success => close host popup; failure => toast via shared helper
      if (result.success) {
        onRequestClosePopup();
      } else {
        warnAndToastSimpleResult(result, t);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, isPreviewMarker, onCloseIncident, onRequestClosePopup, t]);

  const onClickOpenDrawer = useCallback(() => {
    if (isPreviewMarker || !showOpenIncidentButton) {
      return;
    }

    onRequestClosePopup();
    void onOpenIncidentDrawer();
  }, [
    isPreviewMarker,
    onOpenIncidentDrawer,
    onRequestClosePopup,
    showOpenIncidentButton,
  ]);

  const dateTimeValue = useMemo(() => {
    if (!incident.createdAt) {
      return "";
    }
    return formatDate({
      dateISO: incident.createdAt,
      dateFormat: dateTimeFormat,
      timezone,
    });
  }, [dateTimeFormat, incident.createdAt, timezone]);

  const typeColor = (incident.incidentTypeColor ?? "").trim();
  const displayTypeName = (incident.typeName ?? "").trim() || `#${incident.id}`;
  const driverName = (incident.driverName ?? "").trim();
  const driverPhone = formatPhoneLocal((incident.driverPhone ?? "").trim());
  const creatorName = (incident.userCreatedByName ?? "").trim();
  const creatorPhone = formatPhoneLocal((incident.creatorPhone ?? "").trim());

  const hasDriverSection = Boolean(driverName || driverPhone);
  const driverCellDisplay = driverName || NO_VALUE_DISPLAY;

  const driverDetailTooltipContent = useMemo(
    () => (
      <LiveMapIncidentTooltipStackStyled>
        {driverName && <span>{driverName}</span>}
        {driverPhone && (
          <span>
            {t("profile.phone")}: {driverPhone}
          </span>
        )}
        {incident.rideId && (
          <span>
            {t("liveMap.incident.tooltipRideId")} {incident.rideId}
          </span>
        )}
        <span>
          {t("liveMap.incident.tooltipIncidentRef", { id: incident.id })}
        </span>
      </LiveMapIncidentTooltipStackStyled>
    ),
    [driverName, driverPhone, incident.id, incident.rideId, t],
  );

  const creatorTooltipContent = useMemo(
    () => (
      <LiveMapIncidentTooltipStackStyled>
        {creatorName && <span>{creatorName}</span>}
        {creatorPhone && (
          <span>
            {t("profile.phone")}: {creatorPhone}
          </span>
        )}
        {dateTimeValue && (
          <span>
            {t("liveMap.incident.tooltipCreatedAtLine", { at: dateTimeValue })}
          </span>
        )}
      </LiveMapIncidentTooltipStackStyled>
    ),
    [creatorName, creatorPhone, dateTimeValue, t],
  );

  return (
    <div onClick={e => e.stopPropagation()} role="presentation">
      <IncidentPopupRoot>
        <div className={incidentPopupCn.title}>
          <div className={incidentPopupCn.typeRow}>
            <span className={incidentPopupCn.typeTrigger}>
              <span className={incidentPopupCn.typeName}>
                {displayTypeName}
              </span>
              {typeColor && (
                <LiveMapIncidentTypeColorDotStyled
                  $color={typeColor}
                  aria-hidden
                />
              )}
            </span>
          </div>
          {creatorName && (
            <FramerTooltip
              content={creatorTooltipContent}
              mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
            >
              <div className={incidentPopupCn.creator}>{creatorName}</div>
            </FramerTooltip>
          )}
        </div>
        <div className={incidentPopupCn.body}>
          <div className={incidentPopupCn.row}>
            <span className={incidentPopupCn.label}>{dateTimeRowLabel}</span>
            <span className={incidentPopupCn.value}>{dateTimeValue}</span>
          </div>
          <div className={incidentPopupCn.row}>
            <span className={incidentPopupCn.label}>{rideNumberRowLabel}</span>
            <span className={incidentPopupCn.value}>
              {incident.rideId ? String(incident.rideId) : NO_VALUE_DISPLAY}
            </span>
          </div>
          {hasDriverSection && (
            <div className={incidentPopupCn.row}>
              <span className={incidentPopupCn.label}>{driverRowLabel}</span>
              <FramerTooltip
                content={driverDetailTooltipContent}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <span
                  className={clsx(
                    incidentPopupCn.value,
                    incidentPopupCn.valueDriverHint,
                  )}
                >
                  {driverCellDisplay}
                </span>
              </FramerTooltip>
            </div>
          )}
        </div>
        {(showCloseIncidentButton || showOpenIncidentButton) && (
          <div className={incidentPopupCn.footer}>
            {showCloseIncidentButton && (
              <button
                type="button"
                className={incidentPopupCn.close}
                disabled={isPreviewMarker || busy}
                onClick={() => void onClickClose()}
              >
                {closeLabel}
                <ChevronRight sx={{ fontSize: 18 }} />
              </button>
            )}
            {showOpenIncidentButton && (
              <FramerTooltip
                content={openIncidentTooltip}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <button
                  type="button"
                  className={incidentPopupCn.open}
                  aria-label={openIncidentTooltip}
                  disabled={isPreviewMarker}
                  onClick={onClickOpenDrawer}
                  data-e2e="live-map-incident-popup-open-drawer"
                >
                  {canEditIncident ? (
                    <EditOutlined sx={{ fontSize: 20 }} />
                  ) : (
                    <VisibilityOutlined sx={{ fontSize: 20 }} />
                  )}
                </button>
              </FramerTooltip>
            )}
          </div>
        )}
      </IncidentPopupRoot>
    </div>
  );
};
